import React, { useContext } from "react";
import { Menu, Transition } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaUser, FaSignOutAlt } from "react-icons/fa";
import { FaBug } from "react-icons/fa6";
import { FiRefreshCcw } from "react-icons/fi";
import { useCan } from "../../authz/useCan";
import { useCanEnterStaffPortal } from "../../authz/staffPortalAccess";
import config from "../../config";
import { AuthContext } from "../../AuthProvider";
import { useActiveRole } from "../../utils/activeRole";
import { shouldShowParentPortalSwitch } from "./userMenuVisibility";

export function UserMenu({
  onProfilePath = "/profile",
  backendOnline,
  variant = "default",
  showFamilySwitch = false,
  onFamilySwitch,
  buttonClassName = "",
  textClassName = "text-gray-700 dark:text-gray-300",
  menuItemsClassName,
  itemsWrapperClassName,
  onMenuButtonClick,
  onReportIssue,
}) {
  const { keycloak, authenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canEnterStaffPortal = useCanEnterStaffPortal();
  const canManageOwnProfile = useCan("profile.self", "manage");
  const canShowProfile = canEnterStaffPortal || canManageOwnProfile;
  const { setActiveRole } = useActiveRole();
  const canReportIssue = canEnterStaffPortal;

  const isAuthenticated = authenticated;

  const displayName = React.useMemo(() => {
    if (!isAuthenticated) return "User";
    return (
      keycloak?.idTokenParsed?.name ||
      keycloak?.idTokenParsed?.preferred_username ||
      keycloak?.idTokenParsed?.email ||
      "User"
    );
  }, [isAuthenticated, keycloak?.idTokenParsed]);

  const initials = React.useMemo(() => {
    const parts = displayName.trim().split(/\s+/);
    if (parts.length === 0) return "U";
    const letters = parts
      .slice(0, 2)
      .map((part) => part[0])
      .join("");
    return letters.toUpperCase() || "U";
  }, [displayName]);

  const avatarColor = React.useMemo(() => {
    if (variant === "admin") return "bg-indigo-600";
    return "bg-[#4a7a85]";
  }, [variant]);

  const resolvedItemsWrapperClassName = React.useMemo(() => {
    return (
      itemsWrapperClassName ??
      "absolute right-0 mt-2 w-56 origin-top-right z-50 focus:outline-none"
    );
  }, [itemsWrapperClassName]);

  const resolvedMenuItemsClassName = React.useMemo(() => {
    if (menuItemsClassName) return menuItemsClassName;

    if (variant === "parent") {
      return [
        "rounded-xl border border-gray-200/80 bg-white/95 shadow-xl backdrop-blur-sm ring-1 ring-black/5",
        "text-gray-900 dark:border-gray-700/80 dark:bg-gray-800/95 dark:text-gray-200 dark:ring-white/10",
      ].join(" ");
    }

    return [
      "rounded-md border border-gray-100 bg-white shadow-lg ring-1 ring-black/5",
      "text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:ring-white/10",
    ].join(" ");
  }, [menuItemsClassName, variant]);

  // 🔥 Status-glow: online = ademende groene ring, offline = rode ring
  const statusGlowClass = React.useMemo(() => {
    const base =
      "relative after:absolute after:inset-0 after:z-0 after:rounded-full " +
      "after:border-[3px] after:opacity-90 after:content-[''] after:transform after:scale-110 " +
      "after:pointer-events-none";

    if (backendOnline === true) {
      // 🟢 online
      return (
        base +
        " after:border-emerald-400 after:animate-pulse " +
        "after:shadow-[0_0_22px_rgba(16,185,129,0.65)]"
      );
    }

    if (backendOnline === false) {
      // 🔴 offline
      return (
        base +
        " after:border-red-500 " +
        "after:shadow-[0_0_18px_rgba(220,38,38,0.7)]"
      );
    }

    // 🟠 unknown / loading / undefined
    return (
      base +
      " after:border-amber-400 after:animate-pulse " +
      "after:shadow-[0_0_18px_rgba(251,191,36,0.55)]"
    );
  }, [backendOnline]);

  const hasParentRole = React.useMemo(() => {
    if (!authenticated || !keycloak?.tokenParsed) return false;
    const realm = keycloak.tokenParsed?.realm_access?.roles ?? [];
    const clientAll = Object.values(
      keycloak.tokenParsed?.resource_access ?? {}
    );
    const clientRoles = clientAll.flatMap((ra) => ra?.roles ?? []);
    return [...realm, ...clientRoles].some((role) =>
      String(role).toLowerCase().includes("parent")
    );
  }, [authenticated, keycloak?.tokenParsed]);

  if (!isAuthenticated) return null;

  return (
    <Menu as="div" className="relative">
      <Menu.Button
        className={`flex items-center space-x-3 focus:outline-none ${buttonClassName}`}
        onClick={() => {
          onMenuButtonClick?.();
        }}
      >
        <span
          className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold uppercase text-white ${avatarColor} ${statusGlowClass} z-0`}
        >
          <span className="relative z-10">{initials}</span>
        </span>
        <span className={`${textClassName} font-medium`}>{displayName}</span>
      </Menu.Button>
      <Transition
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <Menu.Items
          className={`${resolvedItemsWrapperClassName} ${resolvedMenuItemsClassName}`}
        >
          {canShowProfile && (
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => navigate(onProfilePath)}
                  className={`flex w-full items-center gap-2 px-4 py-2 text-left ${
                    active ? "bg-indigo-50/80 dark:bg-indigo-900/40" : ""
                  } text-gray-900 dark:text-gray-200 hover:bg-indigo-50 hover:text-indigo-700 dark:hover:bg-indigo-900/40 dark:hover:text-indigo-200`}
                >
                  <FaUser className="h-4 w-4" />
                  {t("navbar.profile", { defaultValue: "Profile" })}
                </button>
              )}
            </Menu.Item>
          )}

          {canReportIssue && onReportIssue && (
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => onReportIssue()}
                  className={`flex w-full items-center gap-2 px-4 py-2 text-left text-gray-900 dark:text-gray-200 transition ${
                    active ? "bg-indigo-50/80 dark:bg-indigo-900/40" : ""
                  } hover:bg-indigo-50 hover:text-indigo-700 dark:hover:bg-indigo-900/40 dark:hover:text-indigo-200`}
                >
                  <FaBug className="h-4 w-4 text-indigo-500" />
                  {t("dashboard.report_issue", { defaultValue: "Report an issue" })}
                </button>
              )}
            </Menu.Item>
          )}

          {showFamilySwitch && (
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => {
                    if (onFamilySwitch) {
                      onFamilySwitch();
                  } else if (typeof window !== "undefined") {
                      window.location.reload();
                    }
                  }}
                  className={`flex w-full items-center gap-2 px-4 py-2 text-left text-emerald-700 dark:text-emerald-300 ${
                    active ? "bg-indigo-50/80 dark:bg-indigo-900/40" : ""
                  } hover:bg-indigo-50 hover:text-indigo-700 dark:hover:bg-indigo-900/40 dark:hover:text-indigo-200`}
                >
                  <FiRefreshCcw className="h-4 w-4" />
                  {t("parentPortal.actions.switch_family", {
                    defaultValue: "Switch family",
                  })}
                </button>
              )}
            </Menu.Item>
          )}

          {shouldShowParentPortalSwitch({ hasParentRole, variant }) && (
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => {
                    setActiveRole("parent");
                    navigate("/portal", { replace: true });
                  }}
                  className={`flex w-full items-center gap-2 px-4 py-2 text-left text-indigo-600 dark:text-indigo-300 ${
                    active ? "bg-indigo-50/80 dark:bg-indigo-900/40" : ""
                  } hover:bg-indigo-50 hover:text-indigo-700 dark:hover:bg-indigo-900/40 dark:hover:text-indigo-200`}
                >
                  <FiRefreshCcw className="h-4 w-4" />
                  {t("home.switch_to_parent", {
                    defaultValue: "Switch to Parent",
                  })}
                </button>
              )}
            </Menu.Item>
          )}

          <Menu.Item>
            {({ active }) => (
              <button
                onClick={() =>
                  keycloak?.logout?.({ redirectUri: config.frontendUrl })
                }
                className={`flex w-full items-center gap-2 px-4 py-2 text-left text-red-600 dark:text-red-400 ${
                  active ? "bg-red-50 dark:bg-red-900/30" : ""
                } hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900/30 dark:hover:text-red-200`}
              >
                <FaSignOutAlt className="h-4 w-4" />
                {t("navbar.logout", { defaultValue: "Log out" })}
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
